// ─── Publicité plein écran (interstitiel) — Core ──────────────────
// Structure de config déjà présente (`config/pub.pleinEcran`) mais jamais
// câblée à un composant (20/08) : ce hook la rend enfin active. Web et natif
// sans le module AdMob installé : no-op (le format n'existe que sur natif).
//
// Fréquence (tout piloté par `config/pub.pleinEcran`, MoP Admin) :
//   • `actif`            : coupe-circuit global
//   • `pagesAutorisees`  : liste d'`ongletCle`/noms de page où l'interstitiel
//     peut apparaître (appeler le hook UNIQUEMENT depuis ces pages)
//   • `apresNOuvertures` : nombre de pages vues cette session avant le 1er essai
//   • `apresNMinutes`    : minutes depuis le lancement de l'app avant le 1er essai
//   • `delaiEntreAffichages` : minutes minimum entre deux affichages
//   • `maxParJour`       : plafond quotidien (persistant, `AsyncStorage`)
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ecouterConfigPub } from '../services/config';

let AdMob = null;
if (Platform.OS !== 'web') {
  try { AdMob = require('react-native-google-mobile-ads'); } catch { /* module absent */ }
}

const CLE_ETAT = 'mop_pub_pleinEcran_etat_v1';
const LANCEMENT_LE = Date.now();
let pagesVuesCetteSession = 0;

async function lireEtat() {
  try { return JSON.parse(await AsyncStorage.getItem(CLE_ETAT)) || {}; } catch { return {}; }
}
async function ecrireEtat(etat) {
  try { await AsyncStorage.setItem(CLE_ETAT, JSON.stringify(etat)); } catch { /* pas grave, retente au prochain affichage */ }
}

// À appeler une fois par page NON couverte par ce hook (les compteurs
// « apresNOuvertures » comptent la navigation globale, pas seulement les pages
// autorisées) — le hook lui-même incrémente déjà pour ses propres pages.
export function comptabiliserOuverturePub() { pagesVuesCetteSession++; }

export function useInterstitielPub(nomPage) {
  const cfgPub = useRef({});
  const dejaTente = useRef(false);

  useEffect(() => {
    const unsub = ecouterConfigPub(data => { cfgPub.current = data || {}; });
    return () => unsub();
  }, []);

  useEffect(() => {
    pagesVuesCetteSession++;
    if (!AdMob || dejaTente.current) return;
    const cfg = cfgPub.current.pleinEcran || {};
    if (!cfg.actif) return;
    if (!(cfg.pagesAutorisees || []).includes(nomPage)) return;

    let annule = false;
    (async () => {
      const etat = await lireEtat();
      const jour = new Date().toISOString().slice(0, 10);
      const nAuj = etat.jour === jour ? (etat.nAujourdhui || 0) : 0;
      if (nAuj >= (cfg.maxParJour ?? 3)) return;
      if ((Date.now() - LANCEMENT_LE) / 60000 < (cfg.apresNMinutes ?? 0)) return;
      if (pagesVuesCetteSession < (cfg.apresNOuvertures ?? 0)) return;
      if (etat.dernierAffichageLe
          && (Date.now() - new Date(etat.dernierAffichageLe).getTime()) / 60000 < (cfg.delaiEntreAffichages ?? 60)) return;
      if (annule) return;

      dejaTente.current = true;
      const { InterstitialAd, AdEventType, TestIds } = AdMob;
      const admobCfg = cfgPub.current.admob || {};
      const unitId = Platform.select({
        android: admobCfg.androidInterstitialAdUnitId || TestIds.INTERSTITIAL,
        ios: admobCfg.iosInterstitialAdUnitId || TestIds.INTERSTITIAL,
        default: TestIds.INTERSTITIAL,
      });
      const pub = InterstitialAd.createForAdRequest(unitId);
      const off1 = pub.addAdEventListener(AdEventType.LOADED, () => { if (!annule) pub.show(); });
      const off2 = pub.addAdEventListener(AdEventType.CLOSED, () => {
        ecrireEtat({ jour, nAujourdhui: nAuj + 1, dernierAffichageLe: new Date().toISOString() });
      });
      pub.load();
      return () => { off1(); off2(); };
    })();
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nomPage]);
}
